import React, { useState } from 'react';
import { Modal, Button, Alert, Table, Space } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useI18n } from 'hooks/useI18n';

const PreviewModal = ({ visible, onCancel, onConfirm, validData, uploading }) => {
	const { t } = useI18n();
	const [pageSize, setPageSize] = useState(10);
	const columns = [
		{
			title: '#',
			dataIndex: 'index',
			key: 'index',
			width: 50,
			fixed: 'left',
			render: (_, __, index) => index + 1,
		},
		{
			title: t('bulk_upload.preview.columns.customer_name'),
			dataIndex: 'customerName',
			key: 'customerName',
			width: 150,
			fixed: 'left',
			ellipsis: true,
		},
		{
			title: t('bulk_upload.preview.columns.mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
			width: 120,
		},
		{
			title: t('bulk_upload.preview.columns.gender'),
			dataIndex: 'gender',
			key: 'gender',
			width: 80,
			render: (gender) => (
				<span style={{ textTransform: 'capitalize' }}>
					{gender}
				</span>
			),
		},
		{
			title: t('bulk_upload.preview.columns.dob'),
			dataIndex: 'dob',
			key: 'dob',
			width: 110,
		},
		{
			title: t('bulk_upload.preview.columns.age'),
			dataIndex: 'age',
			key: 'age',
			width: 60,
		},
		{
			title: t('bulk_upload.preview.columns.height'),
			dataIndex: 'height',
			key: 'height',
			width: 100,
		},
		{
			title: t('bulk_upload.preview.columns.weight'),
			dataIndex: 'weight',
			key: 'weight',
			width: 100,
		},
		{
			title: t('bulk_upload.preview.columns.email'),
			dataIndex: 'email',
			key: 'email',
			width: 180,
			ellipsis: true,
		},
		{
			title: t('bulk_upload.preview.columns.coach_id'),
			dataIndex: 'onBoardedBy',
			key: 'onBoardedBy',
			width: 100,
		},
		{
			title: t('bulk_upload.preview.columns.service_id'),
			dataIndex: 'serviceId',
			key: 'serviceId',
			width: 100,
		},
		{
			title: t('bulk_upload.preview.columns.membership_id'),
			dataIndex: 'membershipId',
			key: 'membershipId',
			width: 120,
		},
		{
			title: t('bulk_upload.preview.columns.amount'),
			dataIndex: 'amount',
			key: 'amount',
			width: 100,
			render: (amount) => `₹${amount}`,
		},
		{
			title: t('bulk_upload.preview.columns.paid'),
			dataIndex: 'paid',
			key: 'paid',
			width: 100,
			render: (paid) => `₹${paid}`,
		},
		{
			title: t('bulk_upload.preview.columns.balance'),
			dataIndex: 'balance',
			key: 'balance',
			width: 100,
			render: (balance) => (
				<span style={{ color: balance > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
					₹{balance}
				</span>
			),
		},
		{
			title: t('bulk_upload.preview.columns.payment_method'),
			dataIndex: 'paymentMethod',
			key: 'paymentMethod',
			width: 130,
			render: (method) => (
				<span style={{ textTransform: 'uppercase' }}>
					{method}
				</span>
			),
		},
		{
			title: t('bulk_upload.preview.columns.joining_date'),
			dataIndex: 'joiningDate',
			key: 'joiningDate',
			width: 110,
		},
		{
			title: t('bulk_upload.preview.columns.paid_on'),
			dataIndex: 'paidOn',
			key: 'paidOn',
			width: 110,
		},
		{
			title: t('bulk_upload.preview.columns.next_bill_date'),
			dataIndex: 'nextBillDate',
			key: 'nextBillDate',
			width: 120,
		},
		{
			title: t('bulk_upload.preview.columns.upi_transaction_id'),
			dataIndex: 'upiTransactionId',
			key: 'upiTransactionId',
			width: 180,
			ellipsis: true,
		},
		{
			title: t('bulk_upload.preview.columns.remarks'),
			dataIndex: 'remarks',
			key: 'remarks',
			width: 150,
			ellipsis: true,
		},
	];

	return (
		<Modal
			title={
				<Space>
					<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
					<span style={{ fontSize: 18, fontWeight: 600 }}>{t('bulk_upload.preview.title')}</span>
				</Space>
			}
			open={visible}
			onCancel={onCancel}
			width={1200}
			footer={[
				<Button key="cancel" size="large" onClick={onCancel}>
					{t('common.cancel')}
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={uploading}
					onClick={onConfirm}
					icon={<CheckCircleOutlined />}
					style={{
						minWidth: 150,
						fontWeight: 600,
					}}>
					{t('bulk_upload.preview.confirm_upload')}
				</Button>,
			]}
			style={{ top: 20 }}>
			<div style={{ marginBottom: 16 }}>
				<Alert
					message={
						<span style={{ fontSize: 14 }}>
							{t('bulk_upload.preview.alert_message', {
								count: validData.length,
								plural: validData.length > 1 ? 's' : ''
							})}
						</span>
					}
					type="info"
					showIcon
					style={{ marginBottom: 16 }}
				/>
			</div>

			<div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
				<Table
					dataSource={validData}
					rowKey={(record, index) => index}
					pagination={{
						pageSize,
						showSizeChanger: true,
						pageSizeOptions: ['10', '20', '50', '100'],
						onShowSizeChange: (_, nextPageSize) => setPageSize(nextPageSize),
						showTotal: (total) => `Total ${total} records`,
					}}
					scroll={{ x: 1500 }}
					size="small"
					bordered
					columns={columns}
				/>
			</div>
		</Modal>
	);
};

export default PreviewModal;
